import { z } from 'zod';

export const PlanCode = z.enum(['free', 'basic', 'pro', 'max']);
export type PlanCode = z.infer<typeof PlanCode>;

export type PlanQuotaKey =
  | 'ai_points_monthly'
  | 'crawl_monthly'
  | 'account_sync_notes_monthly'
  | 'self_accounts'
  | 'competitor_accounts'
  | 'l4_skills'
  | 'l5_skills';

export type ModelTier = 'lite' | 'standard' | 'premium';

export type PlanModelCatalogItem = {
  id: string;
  display_name: string;
};

export type PlanModelTierGroup = {
  tier: ModelTier;
  models: PlanModelCatalogItem[];
};

export type PlanCatalogItem = {
  code: PlanCode;
  display_name: string;
  price_monthly_cents: number;
  price_yearly_cents: number;
  currency: string;
  model_tier: ModelTier | 'all';
  model_names: string[];
  storage_mb: number;
  quotas: Partial<Record<PlanQuotaKey, number>>;
  daily_recommendation: {
    auto_refresh_hours: number;
  };
};

export type PlanCatalogResponse = {
  plans: PlanCatalogItem[];
  model_tiers: PlanModelTierGroup[];
};

const PublicPlanQuotaValue = z.number().int().min(-1).max(1_000_000_000);

export const PublicPlanQuotas = z
  .object({
    ai_points_monthly: PublicPlanQuotaValue.optional(),
    crawl_monthly: PublicPlanQuotaValue.optional(),
    account_sync_notes_monthly: PublicPlanQuotaValue.optional(),
    self_accounts: PublicPlanQuotaValue.optional(),
    competitor_accounts: PublicPlanQuotaValue.optional(),
    l4_skills: PublicPlanQuotaValue.optional(),
    l5_skills: PublicPlanQuotaValue.optional(),
  })
  .strict();
export type PublicPlanQuotas = z.infer<typeof PublicPlanQuotas>;

export const PublicPlanFeatures = z
  .object({
    daily_recommendation_auto_refresh_hours: z
      .number()
      .int()
      .min(1)
      .max(24 * 365)
      .optional(),
  })
  .strict();
export type PublicPlanFeatures = z.infer<typeof PublicPlanFeatures>;

export const PublicPlan = z
  .object({
    code: PlanCode,
    display_name: z.string().trim().min(1).max(64),
    price_monthly_cents: z.number().int().nonnegative(),
    price_yearly_cents: z.number().int().nonnegative(),
    currency: z.string().regex(/^[A-Z]{3}$/),
    model_tier: z.enum(['lite', 'standard', 'premium', 'all']),
    storage_mb: z.number().int().nonnegative(),
    quotas: PublicPlanQuotas,
    features: PublicPlanFeatures,
    updated_at: z.string().min(1),
  })
  .strict();
export type PublicPlan = z.infer<typeof PublicPlan>;

export const PublicPlansResponse = z
  .object({
    plans: z.array(PublicPlan),
  })
  .strict();
export type PublicPlansResponse = z.infer<typeof PublicPlansResponse>;
